import React from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

const benefits = [
  "From assessments to drafted care plan in minutes",
  "Digital signature flow with nursing review built-in",
  "Role-based user levels for assessors, nurses, managers",
  "Customisable care plan templates to match your service model",
  "Version control & audit trails for compliance (HIQA/HSE aligned)",
  "Reduced admin workload → more time for client care",
  "GDPR-secure, provider-segregated data",
  "Onboarding support & training during the pilot",
  "Speed to value: measurable efficiency gains in 12 weeks",
  "Insights dashboards to track adoption and quality"
];

export default function PilotProgrammeSection() {
  const [isPaused, setIsPaused] = React.useState(false);
  const [hasAnimated, setHasAnimated] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  const autoplay = React.useRef(
    Autoplay({ delay: 3500, stopOnInteraction: false, stopOnMouseEnter: true })
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true,
      align: 'start',
      slidesToScroll: 1
    },
    [autoplay.current]
  );

  const [selectedIndex, setSelectedIndex] = React.useState(0);

  React.useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on('select', onSelect);
    onSelect();

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  const toggleAutoplay = () => {
    if (autoplay.current) {
      if (isPaused) {
        autoplay.current.play();
      } else {
        autoplay.current.stop();
      }
      setIsPaused(!isPaused);
    }
  };

  const scrollPrev = () => emblaApi?.scrollPrev();
  const scrollNext = () => emblaApi?.scrollNext();

  return (
    <section 
      id="pilot-programme" 
      ref={sectionRef}
      className="py-16 md:py-20 text-white relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #13384B 0%, #005D63 60%, #13384B 100%)' }}
    >
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full blur-3xl" style={{ background: '#51BF9D' }}></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full blur-3xl" style={{ background: '#A6CE39' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Header */}
        <div 
          className={`text-center mb-12 transition-all duration-700 ${
            hasAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Join the Allwayswithyou Pilot Programme
          </h2>
          <p className="text-lg text-slate-300 max-w-3xl mx-auto">
            Transform care planning across home, community, and specialist service.
          </p>
        </div>

        {/* Carousel */}
        <div 
          className={`relative mb-8 transition-all duration-700 delay-200 ${
            hasAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-4">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="flex-[0_0_100%] min-w-0 md:flex-[0_0_calc(50%-8px)] lg:flex-[0_0_calc(33.333%-11px)]"
                  role="group"
                  aria-roledescription="slide"
                  aria-label={`Benefit ${index + 1} of ${benefits.length}`}
                >
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 h-full border border-white/20 hover:bg-white/15 transition-colors">
                    <div className="flex items-start gap-3">
                      <svg 
                        className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: '#51BF9D' }}
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-white text-base leading-relaxed">
                        {benefit}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Buttons */}
          <button
            onClick={scrollPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-3 rounded-full transition-all border border-white/30"
            aria-label="Previous benefit"
            data-testid="button-carousel-prev"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={scrollNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-3 rounded-full transition-all border border-white/30"
            aria-label="Next benefit"
            data-testid="button-carousel-next"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Dots Indicators */}
        <div 
          className="flex justify-center items-center gap-4 mb-10"
          role="tablist"
          aria-label="Benefit carousel navigation"
        >
          <button
            onClick={toggleAutoplay}
            className="text-white/70 hover:text-white transition-colors p-2"
            aria-label={isPaused ? "Play carousel" : "Pause carousel"}
            data-testid="button-carousel-toggle"
          >
            {isPaused ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
              </svg>
            )}
          </button>
          <div className="flex gap-2" aria-live="polite" aria-atomic="true">
            {benefits.map((_, index) => (
              <button
                key={index}
                onClick={() => emblaApi?.scrollTo(index)}
                className={`h-2 rounded-full transition-all ${
                  index === selectedIndex 
                    ? 'w-8 bg-white' 
                    : 'w-2 bg-white/40 hover:bg-white/60'
                }`}
                aria-label={`Go to benefit ${index + 1}`}
                aria-current={index === selectedIndex ? 'true' : 'false'}
                role="tab"
              />
            ))}
          </div>
        </div>

        {/* CTAs */}
        <div 
          className={`text-center space-y-4 transition-all duration-700 delay-300 ${
            hasAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <a
            href="mailto:info@allwayswithyou.com?subject=Pilot%20Programme%20Inquiry&body=Hi%20Allwayswithyou%20team%2C%0D%0A%0D%0AWe%E2%80%99re%20interested%20in%20joining%20your%20pilot%20programme.%20Please%20share%20next%20steps.%0D%0A%0D%0AOrganisation%3A%0D%0AContact%20Name%3A%0D%0APhone%3A%0D%0AEstimated%20Number%20of%20Clients%3A%0D%0ATimeline%3A%0D%0A%0D%0AThanks%2C"
            className="inline-flex px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-xl hover:shadow-2xl hover:scale-105 hover:opacity-90"
            style={{ background: '#51BF9D', color: '#13384B' }}
            data-testid="button-request-pilot-access"
          >
            Request Pilot Access
          </a>
          <div>
            <a
              href="/blog/join-pilot-programme"
              className="text-white/80 hover:text-white font-semibold inline-flex items-center gap-2 hover:underline"
              data-testid="link-learn-more-pilot"
            >
              Learn more about the pilot
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
